import React, { useState, useContext, useEffect, useRef, useMemo, Suspense, lazy } from "react"
import { useHistory, useLocation } from "react-router-dom"
import clsx from "clsx"
import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  Button,
} from "@material-ui/core"
import MenuIcon from "@material-ui/icons/Menu"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import MainListItems from "./MainListItems"
import UserModal from "../components/UserModal"
import { AuthContext } from "../context/Auth/AuthContext"
import BackdropLoading from "../components/BackdropLoading"
import toastError from "../errors/toastError"
import icon from "../assets/icon blue.svg"
import iconDark from "../assets/icon white.svg"
import logo from "../assets/logo.png"
import logoDark from "../assets/logoDark.png"
import { SocketContext } from "../context/Socket/SocketContext"
import UnifiedPopover from "../components/UnifiedPopover"
import { ColorModeContext } from "../layout/themeContext"
import { ChevronDown, ChevronRight, ListFilter, Plus } from "lucide-react"
import { Sun, UserRound, LogOut, Moon, AppWindowMac } from "lucide-react"
import { currentRouterName } from "../hooks/useCurrentRouterName"
import FilterModal from "../components/FiltroKanbanModal/FiltroKanbanModal"
import useStyles from "./styles"
import PathUtils from "./components/PathUtils"
import usePlans from "../hooks/usePlans"
import useSearchParamStore from "../stores/SearchParamStore"
import Cookies from "js-cookie"
import getTipoDeDispositivo from "../helpers/getTipoDeDispositivo"
import { usuarioCampanhasEmailLiberado } from "../helpers/usuarioCampanhasEmailLiberado"
import { useFilaStore } from "../stores/filaStore"
import NovaConversaModal from "../components/NovaConversaModal"
import TicketsSocketInitializer from "../components/TicketsSocketInitializer"
import QuickMessageSocketInitializer from "../components/QuickMessageSocketInitializer"
import UserSocketInitializer from "../components/UserSocketInitializer"
import SituacaoSocketInitializer from "../components/SituacaoSocketInitializer"
import AvatarPerfilUsuario from "./components/AvatarPerfilUsuario"
import { useQueryClient } from "react-query"
import { agendarPrefetchKanbanFluxoEmSegundoPlano, cancelarAgendamentoIdle } from "../hooks/useKanbanQueries/prefetchKanbanFluxoInicial"
import { useFiltrosKanbanStore, filtrosKanbanTemFiltroAtivo } from "../stores/filtrosKanbanStore"
import { prefetchDashboardCompleto } from "../hooks/useDashboardQueries/prefetchDashboardPeriodo"

const Kanban = lazy(() => import("../pages/Kanban"))
const Schedules = lazy(() => import("../pages/Schedules"))

/**
 * Retorna as iniciais do nome do usuário.
 * Se o nome tiver apenas uma palavra, retorna a primeira letra.
 * Se o nome tiver múltiplas palavras, retorna a primeira letra da primeira e última palavra.
 * @param {Object} user - Objeto do usuário contendo a propriedade name
 * @returns {string} Iniciais do nome do usuário
 */
const obterIniciaisDoNome = (user) => {
  if (user && user.name) {
    if (!user.name.includes(" ")) {
      return user.name[0]
    }

    const mPalavras = user.name.split(" ")
    const cPrimeiraLetra = mPalavras[0][0]
    const cUltimaLetra = mPalavras[mPalavras.length - 1][0]

    return cPrimeiraLetra + cUltimaLetra
  }
  return ""
}

const LoggedInLayout = ({ children }) => {
  // ==================== Hooks de Roteamento ====================
  const location = useLocation()
  const history = useHistory()

  // ==================== Contextos ====================
  const { handleLogout, loading, user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)
  const { setColorMode } = useContext(ColorModeContext)

  // ==================== Estados de UI - Menus e Modais ====================
  const [aparenciaOpen, setAparenciaOpen] = useState(false)
  const [aparenciaMenuAnchorEl, setAparenciaMenuAnchorEl] = useState(null)
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false)
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  // ==================== Estados do Drawer ====================
  /** Estado que controla se o drawer está aberto ou fechado, persistido no localStorage */
  const [lDrawerAberto, setlDrawerAberto] = useState(() => {
    try {
      const cDrawerAbertoPersistido = localStorage.getItem("drawerOpen")
      if (cDrawerAbertoPersistido !== null) return cDrawerAbertoPersistido === "true"
    } catch (e) {
      // ignore errors (e.g., localStorage not available)
    }
    return window.innerWidth > 600
  })

  /**
   * Atualiza o estado do drawer e persiste a preferência no localStorage.
   * @param {boolean|Function} valorOuAtualizador - Novo valor ou função atualizadora
   */
  const atualizarlDrawerAberto = (valorOuAtualizador) => {
    setlDrawerAberto((lAnterior) => {
      const lProximo = typeof valorOuAtualizador === "function" ? valorOuAtualizador(lAnterior) : valorOuAtualizador
      try {
        localStorage.setItem("drawerOpen", String(lProximo))
      } catch (e) {
        // ignore localStorage errors
      }
      return lProximo
    })
  }

  /** Variante do drawer: 'permanent' para desktop, 'temporary' para mobile */
  const [drawerVariant, setDrawerVariant] = useState("permanent")

  // ==================== Estados de Filtros e Layout ====================
  const oFiltros = useFiltrosKanbanStore((state) => state.oFiltros)
  const filtrosDefinir = useFiltrosKanbanStore((state) => state.filtrosDefinir)
  const [lLayoutTicketsCompactos, setLayoutTicketsCompactos] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    useFiltrosKanbanStore.persist.rehydrate()
  }, [user?.id])

  // ==================== Estados de Tema ====================
  const currentMode = Cookies.get("preferredTheme")
  const [currentColorMode, setCurrentColorMode] = useState(currentMode)
  const theme = useTheme()
  const classes = useStyles({ drawerOpen: lDrawerAberto })

  // ==================== Estados de Controle de Acesso ====================
  const profile = user?.profile
  const { setOpenModal, openModal } = useSearchParamStore()
  const { getPlanCompany } = usePlans()
  const queryClient = useQueryClient()

  /** Controla se o usuário tem acesso ao módulo Kanban baseado no plano */
  const [lKanbanAcessar, setlKanbanAcessar] = useState(true)
  /** Controla se o usuário tem acesso ao módulo de Agendamentos baseado no plano */
  const [lAgendamentosAcessar, setAgendamentosAcessar] = useState(true)
  /** Array de rotas permitidas baseado no perfil do usuário */
  const [mRotas, setmRotas] = useState([])

  // ==================== Constantes Calculadas ====================
  const isMobile = window.innerWidth <= 600
  const isConversasWithUUID = location.pathname.match(/^\/conversas\/[0-9a-fA-F-]{36}$/)

  // ==================== Refs ====================
  const aparenciaMenuRef = useRef(null)

  // ==================== Effects ====================
  /**
   * Effect: Define as rotas permitidas baseado no perfil do usuário.
   * Perfis: admin (acesso total), supervisor (acesso limitado), user (acesso básico)
   */
  useEffect(() => {
    setmRotas([])
    if (profile === "admin") {
      setmRotas([
        "/conversas",
        "/kanban",
        "/schedules",
        "/contacts",
        "/situacoes",
        "/contact-lists",
        "/contact-lists/:contactListId/contacts",
        "/campaign/",
        "/campaigns",
        "/campaigns-config",
        "/quick-messages",
        "/announcements",
        "/connections",
        "/dashboard",
        "/queues",
        "/users",
        "/portal-cliente",
        "/settings",
      ])
    } else if (profile === "supervisor") {
      setmRotas(["/conversas", "/kanban", "/schedules", "/contacts", "/situacoes", "/quick-messages"])
    } else {
      if (usuarioCampanhasEmailLiberado(user?.email)) {
        setmRotas([
          "/conversas",
          "/kanban",
          "/schedules",
          "/contacts",
          "/situacoes",
          "/quick-messages",
          "/campaign/",
          "/campaigns",
          "/campaigns-config",
          "/contact-lists",
          "/contact-lists/:contactListId/contacts",
        ])
      } else {
        setmRotas(["/conversas", "/kanban", "/schedules", "/contacts", "/situacoes", "/quick-messages"])
      }
    }
  }, [profile, user?.email])

  /**
   * Effect: Verifica as permissões do plano da empresa.
   * Define se o usuário tem acesso aos módulos Kanban e Agendamentos.
   */
  useEffect(() => {
    const cEmpresaId = localStorage.getItem("companyId")
    let lCancelado = false

    const carregarPlano = async () => {
      const oPlano = await getPlanCompany(undefined, cEmpresaId)
      if (lCancelado) return
      setlKanbanAcessar(oPlano.plan.useKanban)
      setAgendamentosAcessar(oPlano.plan.useSchedules)
    }

    const nAgendamento =
      typeof requestIdleCallback !== "undefined"
        ? requestIdleCallback(() => {
            carregarPlano()
          })
        : setTimeout(carregarPlano, 0)

    return () => {
      lCancelado = true
      if (typeof requestIdleCallback !== "undefined" && typeof cancelIdleCallback !== "undefined") {
        cancelIdleCallback(nAgendamento)
      } else {
        clearTimeout(nAgendamento)
      }
    }
  }, [location])

  useEffect(() => {
    if (!mRotas.includes("/dashboard")) return
    if (location.pathname.startsWith("/conversas")) return

    const agendarPrefetchDashboard = () => prefetchDashboardCompleto()
    const nAgendamento =
      typeof requestIdleCallback !== "undefined"
        ? requestIdleCallback(agendarPrefetchDashboard)
        : setTimeout(agendarPrefetchDashboard, 2000)

    return () => {
      if (typeof requestIdleCallback !== "undefined" && typeof cancelIdleCallback !== "undefined") {
        cancelIdleCallback(nAgendamento)
      } else {
        clearTimeout(nAgendamento)
      }
    }
  }, [mRotas, location.pathname])

  /**
   * Effect: Controla o comportamento responsivo do drawer e layout compacto.
   * - Mobile (≤600px): Drawer temporary (modal) e fechado por padrão
   * - Desktop (>600px): Drawer permanent e aberto por padrão
   * - Conversas em telas pequenas: Layout compacto ativado
   */
  useEffect(() => {
    const handleLayoutCompacto = () => {
      const lViewportMobile = window.innerWidth <= 600
      const lViewportPequeno = window.innerWidth <= 1300 || window.innerHeight <= 650
      const lRotaConversas = location.pathname.startsWith("/conversas")
      const lCompacto = lViewportPequeno && lRotaConversas
      setLayoutTicketsCompactos(lCompacto)

      // Em mobile, Drawer deve ser modal (temporary) e ficar escondido até o usuário clicar
      if (lViewportMobile) {
        if (drawerVariant !== "temporary") setDrawerVariant("temporary")
      } else {
        // Em telas maiores, Drawer permanece permanente (expansível)
        if (drawerVariant !== "permanent") setDrawerVariant("permanent")
        let cDrawerAbertoPersistido = null
        try {
          cDrawerAbertoPersistido = localStorage.getItem("drawerOpen")
        } catch (e) {
          cDrawerAbertoPersistido = null
        }
        if (cDrawerAbertoPersistido === null && !lDrawerAberto) atualizarlDrawerAberto(true)
      }
    }

    handleLayoutCompacto()
    window.addEventListener("resize", handleLayoutCompacto)
    return () => window.removeEventListener("resize", handleLayoutCompacto)
  }, [location.pathname, drawerVariant])

  /**
   * Effect: Gerencia a conexão WebSocket e monitora o status do usuário.
   * - Detecta login simultâneo em outro dispositivo do mesmo tipo
   * - Emite status do usuário a cada 5 minutos
   */
  useEffect(() => {
    const companyId = localStorage.getItem("companyId")
    const userId = localStorage.getItem("userId")
    const cTipoDeDispositivo = getTipoDeDispositivo()

    const socket = socketManager.getSocket(companyId)

    socket.on(`company-${companyId}-auth`, (data) => {
      if (data.user.id === +userId && data.tipoDeDispositivo === cTipoDeDispositivo) {
        toastError("Sua conta foi acessada em outro dispositivo do mesmo tipo.")
        setTimeout(() => {
          localStorage.clear()
          window.location.reload()
        }, 1000)
      }
    })

    socket.emit("userStatus")
    const interval = setInterval(
      () => {
        socket.emit("userStatus")
      },
      1000 * 60 * 5,
    )

    return () => {
      socket.disconnect()
      clearInterval(interval)
    }
  }, [socketManager])
  /*
   * @property {Function} invalidateCache
   */
  const { invalidateCache } = useFilaStore()

  useEffect(() => {
    const companyId = localStorage.getItem("companyId")
    const socket = socketManager.getSocket(companyId)

    socket.on(`company-${companyId}-queue`, () => {
      invalidateCache()
    })

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  const mFilasUsuario = useMemo(() => {
    if (!user) return [] // ou retorne um array vazio como fallback

    return user.allTicket === "enabled" || user.profile === "admin"
      ? [...(user.queues || []), { id: null, name: "Sem fila" }]
      : user.queues || []
  }, [user])

  useEffect(() => {
    if (!lKanbanAcessar || !user?.id) return
    if (location.pathname.startsWith("/kanban") || location.pathname.startsWith("/fluxo")) return

    const lEmConversas = location.pathname.startsWith("/conversas")
    const oControle = agendarPrefetchKanbanFluxoEmSegundoPlano(queryClient, {
      lIncluirFluxo: false,
      lEsperarConversas: lEmConversas,
      mFilasUsuario,
    })

    return () => {
      oControle.cancelar()
    }
  }, [lKanbanAcessar, user?.id, queryClient, location.pathname, profile, mFilasUsuario])

  // ==================== Funções Auxiliares ====================
  /**
   * Controla o acesso às páginas baseado em permissões do plano e perfil do usuário.
   * Redireciona para /conversas se o usuário não tiver acesso à rota solicitada.
   * @returns {ReactNode|null} Componente da página ou null se não autorizado
   */
  const acessarPagina = () => {
    if (location.pathname.includes("/schedules")) {
      if (lAgendamentosAcessar) {
        return (
          <Suspense fallback={null}>
            <Schedules />
          </Suspense>
        )
      } else {
        history.push("/conversas")
        return null
      }
    }

    if (location.pathname.includes("/kanban")) {
      if (lKanbanAcessar) {
        return (
          <Suspense fallback={null}>
            <Kanban mFilasUsuario={mFilasUsuario} />
          </Suspense>
        )
      } else {
        history.push("/conversas")
        return null
      }
    }

    if (location.pathname.includes("/fluxo")) {
      history.push("/conversas")
      return null
    }

    const rotaLista = mRotas.some((rota) => location.pathname.includes(rota))

    if (rotaLista) {
      return children
    } else {
      history.push("/conversas")
      return null
    }
  }

  // ==================== Handlers de Menu ====================
  /** Abre o menu de perfil do usuário */
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
  }

  /** Fecha o menu de perfil */
  const handleCloseMenu = () => {
    setMenuOpen(false)
    setAnchorEl(null)
  }

  /** Abre o submenu de aparência (tema claro/escuro/automático) */
  const handleOpenAparencia = (event) => {
    setAparenciaOpen(true)
    setAparenciaMenuAnchorEl(event.currentTarget)

    // Transferir o foco para o menu de aparência
    setTimeout(() => {
      if (aparenciaMenuRef.current) {
        aparenciaMenuRef.current.focus()
      }
    }, 0)
  }

  const handleCloseAparencia = () => {
    setAparenciaOpen(false)
    setAparenciaMenuAnchorEl(null)
  }

  /** Abre o modal de edição de perfil do usuário */
  const handleOpenUserModal = () => {
    setUserModalOpen(true)
    handleCloseMenu()
  }

  /** Realiza logout do usuário */
  const handleClickLogout = () => {
    handleCloseMenu()
    handleLogout()
  }

  // ==================== Handlers de Filtros e Tickets ====================
  /**
   * Aplica os filtros selecionados no Kanban ou Fluxo de Conversas.
   * @param {Object} filtros - Objeto contendo os filtros selecionados
   */
  const handleApplyFilters = ({
    queues = [],
    situacoes = [],
    users = [],
    empresa = "",
    motivo = [],
    contatos = [],
    contatosSelecionados = [],
    lidas,
  }) => {
    const oFiltros = {
      queues,
      situacoes,
      users,
      empresa,
      motivo,
      contatos,
      contatosSelecionados,
      lidas,
    }
    filtrosDefinir(oFiltros)

    setOpenModal(false)
  }

  /**
   * Fecha o drawer em dispositivos móveis ao navegar.
   * Mantém aberto em desktops.
   */
  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      atualizarlDrawerAberto(false)
    }
  }

  /**
   * Fecha o modal de novo ticket e navega para o ticket criado, se disponível.
   * @param {Object} ticket - Objeto do ticket criado
   */
  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false)
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/conversas/${ticket.uuid}`)
    }
  }

  // ==================== Funções de Verificação ====================
  /**
   * Verifica se há filtros ativos no Kanban.
   * @returns {boolean} True se houver algum filtro ativo
   */
  const isFiltroAtivoKanban = () => filtrosKanbanTemFiltroAtivo(oFiltros, mFilasUsuario)

  /**
   * Verifica se há filtros ativos no Fluxo de Conversas.
   * @returns {boolean} True se houver algum filtro ativo
   */
  const isFiltroAtivoFluxo = () => {
    if (Object.keys(oFiltros).length === 0) {
      return false
    }
    return (
      oFiltros.queues?.length !== 0 ||
      oFiltros.situacoes?.length !== 0 ||
      oFiltros.users?.length !== 0 ||
      oFiltros.contatos?.length !== 0 ||
      !oFiltros.lidas
    )
  }

  // ==================== Renderização ====================
  // Exibe loading enquanto carrega dados de autenticação
  if (loading) {
    return <BackdropLoading />
  }

  return (
    <div className={classes.root}>
      <TicketsSocketInitializer />
      <QuickMessageSocketInitializer />
      <UserSocketInitializer />
      <SituacaoSocketInitializer />
      <NovaConversaModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket)
        }}
      />

      <Drawer
        variant={drawerVariant}
        className={lDrawerAberto ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(classes.drawerPaper, !lDrawerAberto && classes.drawerPaperClose),
        }}
        open={lDrawerAberto}
      >
        <div>
          <div className={classes.toolbarIcon}>
            {lDrawerAberto ? (
              <a
                style={{ padding: 15, justifyContent: "start", cursor: "pointer" }}
                onClick={() => atualizarlDrawerAberto(!lDrawerAberto)}
              >
                <MenuIcon style={{ marginBottom: 2, marginRight: 5 }} />
                <img
                  alt="logo"
                  src={theme.mode === "light" ? logo : logoDark}
                  className={clsx(theme.mode === "light" ? classes.logo : classes.logoDark, classes.logoImage)}
                  style={{
                    width: "60%",
                    opacity: lDrawerAberto ? "1" : "0",
                    pointerEvents: "none",
                    userSelect: "none",
                    marginLeft: "16px",
                  }}
                />
              </a>
            ) : (
              <IconButton
                style={{ padding: 15, justifyContent: "start", borderRadius: `${lDrawerAberto ? "4%" : "25%"}` }} // Changed from "start" to "center"
                onClick={() => atualizarlDrawerAberto(!lDrawerAberto)}
              >
                <img
                  alt="icon"
                  src={theme.mode === "light" ? icon : iconDark}
                  className={classes.iconBar}
                  style={{
                    width: "24px",
                    opacity: lDrawerAberto ? "0" : "1",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              </IconButton>
            )}
          </div>
        </div>
        <div>
          <List>
            <MainListItems drawerClose={drawerClose} collapsed={!lDrawerAberto} drawerOpen={lDrawerAberto} />
          </List>
        </div>
      </Drawer>

      <UserModal open={userModalOpen} onClose={() => setUserModalOpen(false)} userId={user?.id} />
      {!((isMobile || lLayoutTicketsCompactos) && isConversasWithUUID) ? (
        <AppBar
          position="absolute"
          className={clsx(classes.appBar, lDrawerAberto && classes.appBarShift)}
          color="primary"
        >
          <Toolbar variant="dense" className={classes.toolbar}>
            <IconButton
              edge="start"
              variant="contained"
              aria-label="open drawer"
              onClick={() => atualizarlDrawerAberto(!lDrawerAberto)}
              className={clsx(classes.menuButton, lDrawerAberto && classes.menuButtonHidden)}
              style={{ flex: "0 0 48px", padding: 10, justifyContent: "start", borderRadius: "5%" }}
            >
              {window.innerWidth <= 960 &&
                (() => {
                  const logoClassName = clsx(
                    theme.mode === "light" ? classes.logo : classes.logoDark,
                    classes.logoImage,
                  )

                  return (
                    <img
                      alt="logo"
                      src={theme.mode === "light" ? logo : logoDark}
                      className={logoClassName}
                      style={{ width: "9rem", pointerEvents: "none", userSelect: "none" }}
                    />
                  )
                })()}
            </IconButton>

            <Typography component="h2" variant="h6" color="inherit" noWrap className={classes.title}>
              <div
                className={classes.textWelcome}
                style={{
                  paddingLeft:
                    currentRouterName(location) === "Kanban" || currentRouterName(location) === "Fluxo de conversas"
                      ? 42
                      : currentRouterName(location) === "Conversas"
                        ? 16
                        : 24,
                }}
              >
                {currentRouterName(location)}
              </div>
            </Typography>

            <PathUtils />

            {(location.pathname === "/kanban" || location.pathname === "/fluxo") && window.innerWidth > 1250 && (
              <>
                <Button
                  variant="contained"
                  startIcon={<Plus />}
                  color="primary"
                  className={classes.buttonNova}
                  disableElevation
                  onClick={() => setNewTicketModalOpen(true)}
                >
                  Nova
                </Button>
                <IconButton
                  variant="contained"
                  className={clsx(classes.filterButton, isFiltroAtivoKanban && classes.filterButtonActive)}
                  onClick={() => setOpenModal(true)}
                >
                  <ListFilter
                    color={
                      location.pathname === "/kanban"
                        ? isFiltroAtivoKanban()
                          ? theme.palette.primary200
                          : theme.palette.neutral300
                        : isFiltroAtivoFluxo()
                          ? theme.palette.primary200
                          : theme.palette.neutral300
                    }
                  />
                </IconButton>
              </>
            )}

            {location.pathname.includes("conversas") && window.innerWidth > 550 && (
              <Button
                variant="contained"
                startIcon={<Plus />}
                color="primary"
                className={classes.buttonNova}
                disableElevation
                onClick={() => setNewTicketModalOpen(true)}
              >
                Nova
              </Button>
            )}
            {window.innerWidth > 960 && (
              <Divider
                orientation="vertical"
                flexItem
                style={{
                  marginLeft: "16px",
                  marginRight: "16px",
                  height: "32px",
                  marginTop: "12px",
                }}
              />
            )}
            {/*<IconButton
            edge="start"
            onClick={toggleColorMode}
            className={classes.elementBar}
          >
            <Sun strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />
          </IconButton>
          */}
            <UnifiedPopover />
            <div>
              <IconButton
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                variant="contained"
                className={classes.elementBarCircle}
                disableRipple
                disableFocusRipple
              >
                <AvatarPerfilUsuario user={user} className={classes.circleMenuPerfilAppBar} />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                classes={{ paper: classes.menuPerfilPaper }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={menuOpen}
                onClose={handleCloseMenu}
                className={aparenciaOpen ? "menu-frozen" : ""} // Adiciona classe ao congelar
              >
                <div className={classes.menuPerfil}>
                  <AvatarPerfilUsuario user={user} className={classes.circleMenuPerfil} />
                  <div style={{ flexDirection: "column" }}>
                    <p className={classes.UserName}>{user.name}</p>
                    <p className={classes.CompanyName}>{user?.company?.name}</p>
                  </div>
                </div>

                <div className={classes.menu}>
                  <MenuItem onClick={handleOpenUserModal} style={{ borderRadius: "12px" }}>
                    <UserRound strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />
                    <div className={classes.textMenuPerfil}>Minha conta</div>
                  </MenuItem>
                  <MenuItem
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderRadius: "12px",
                    }}
                    onClick={handleOpenAparencia}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Sun strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />

                      <div className={classes.textMenuPerfil}>Aparência</div>
                    </div>

                    {aparenciaOpen && <ChevronDown strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />}
                    {!aparenciaOpen && <ChevronRight strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />}
                  </MenuItem>

                  <MenuItem onClick={handleClickLogout} style={{ borderRadius: "12px" }}>
                    <LogOut strokeWidth={1.6} className={classes.iconMenu} size={"24px"} />
                    <div className={classes.textMenuPerfil}>Sair</div>
                  </MenuItem>
                </div>
              </Menu>
            </div>
          </Toolbar>
        </AppBar>
      ) : null}
      <main
        className={
          (isMobile || lLayoutTicketsCompactos) && isConversasWithUUID ? classes.contentMobile : classes.content
        }
      >
        {acessarPagina()}
      </main>
      <Menu
        id="aparencia-menu"
        className={classes.menuAparenciaRoot}
        classes={{ paper: classes.menuAparencia }}
        anchorEl={aparenciaMenuAnchorEl}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(aparenciaMenuAnchorEl)}
        onClose={handleCloseAparencia}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        ref={aparenciaMenuRef}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            handleCloseAparencia()
          }
        }}
      >
        <div className={classes.menuAparenciaItens}>
          <Button
            className={classes.button}
            style={{
              backgroundColor: currentColorMode === "light" ? theme.palette.background300 : "",
              color: currentColorMode === "light" ? theme.palette.primary200 : theme.palette.neutral400,
              justifyContent: "space-between",
              alignItems: "center",
            }}
            onClick={() => {
              setCurrentColorMode("light")
              setColorMode("light")
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Sun
                strokeWidth={1.6}
                className={classes.iconMenu}
                size={"24px"}
                color={currentColorMode === "light" ? theme.palette.primary200 : theme.palette.neutral400}
              />
              <div style={{ padding: "2px 0px 2px 16px" }}> Claro </div>
            </div>
            {currentColorMode === "light" && (
              <CheckCircleIcon size={"24px"} color={theme.palette.selectedButtonColor} />
            )}
          </Button>
          <Button
            className={classes.button}
            style={{
              backgroundColor: currentColorMode === "dark" ? theme.palette.background300 : "",
              color: currentColorMode === "dark" ? theme.palette.primary200 : theme.palette.neutral400,
              justifyContent: "space-between",
              alignItems: "center",
            }}
            onClick={() => {
              setCurrentColorMode("dark")
              setColorMode("dark")
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Moon
                strokeWidth={1.6}
                className={classes.iconMenu}
                size={"24px"}
                color={currentColorMode === "dark" ? theme.palette.primary200 : theme.palette.neutral400}
              />
              <div style={{ padding: "2px 0px 2px 16px" }}> Escuro </div>
            </div>
            {currentColorMode === "dark" && <CheckCircleIcon size={"24px"} color={theme.palette.selectedButtonColor} />}
          </Button>
          <Button
            className={classes.button}
            style={{
              backgroundColor: currentColorMode === "auto" ? theme.palette.background300 : "",
              color: currentColorMode === "auto" ? theme.palette.primary200 : theme.palette.neutral400,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            onClick={() => {
              setCurrentColorMode("auto")
              setColorMode("auto")
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <AppWindowMac
                strokeWidth={1.6}
                className={classes.iconMenu}
                size={"24px"}
                color={currentColorMode === "auto" ? theme.palette.primary200 : theme.palette.neutral400}
              />
              <div style={{ padding: "2px 0px 2px 16px" }}> Automático </div>
            </div>
            {currentColorMode === "auto" && <CheckCircleIcon size={"24px"} color={theme.palette.selectedButtonColor} />}
          </Button>
        </div>
      </Menu>
      <style>{`
        .menu-frozen {
          pointer-events: none; /* Desativa cliques */
        }
      `}</style>
      {(location.pathname === "/kanban" || location.pathname === "/fluxo") && (
        <FilterModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onApplyFilters={handleApplyFilters}
          mFilasUsuario={mFilasUsuario}
          filters={oFiltros}
        />
      )}
    </div>
  )
}

export default LoggedInLayout
